// Aerial Yoga Frome - Service Page
import { useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Check, Clock, User, MapPin } from "lucide-react";
import Navigation from "../../components/shared/Navigation";
import Footer from "../../components/shared/Footer";
import { usePageMeta, generateServiceSchema, generateFAQSchema, SchemaScript } from "../../components/shared/SEOHelper";

const AerialYogaFrome = () => {
  usePageMeta({
    title: "Aerial Yoga Frome | Hammock Yoga | Annelie Lily",
    description: "Private aerial yoga sessions in Frome, Somerset with Annelie Lily. Use a fabric hammock to deepen poses, build strength, and experience yoga in a whole",
    canonical: "https://annelielily.com/yoga-classes/aerial-yoga"
  });

  const serviceSchema = generateServiceSchema({
    name: "Aerial Yoga in Frome",
    description: "Private aerial yoga sessions using fabric hammocks to support and deepen yoga poses.",
    serviceType: "Aerial Yoga Class",
    url: "https://annelielily.com/yoga-classes/aerial-yoga",
    image: "https://seventhsensehealing.com/wp-content/uploads/2023/02/aerial-yoga-1-scaled-1.jpg"
  });

  const faqs = [
    {
      question: "Do I need yoga experience for aerial yoga?",
      answer: "No previous yoga or acrobatics experience is needed. Annelie Lily will guide you through everything from the beginning, adapting to your level."
    },
    {
      question: "Is aerial yoga safe?",
      answer: "Yes, when practiced with a qualified instructor. The hammock is securely rigged and can support significant weight. Annelie Lily is trained through One Yoga and Wellness as an aerial yoga teacher."
    },
    {
      question: "Can aerial yoga help with back pain?",
      answer: "Many people find aerial yoga helpful for back issues. The hammock allows gentle spinal decompression and supported stretches that can relieve pressure on the spine."
    },
    {
      question: "What should I wear?",
      answer: "Fitted clothing that covers the backs of your knees and underarms works best — it prevents the fabric from pinching. Avoid loose, baggy clothes and remove jewellery."
    }
  ];

  const faqSchema = generateFAQSchema(faqs);

  return (
    <div className="min-h-screen bg-cream">
      <SchemaScript schema={serviceSchema} />
      <SchemaScript schema={faqSchema} />
      <Navigation />
      
      {/* Hero */}
      <section className="pt-28 pb-16 px-6 md:px-12 bg-muted">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-10 items-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <span className="text-xs uppercase tracking-[0.2em] text-gold font-semibold mb-3 block">
                Aerial Yoga in Frome
              </span>
              <h1 className="font-heading text-4xl md:text-5xl font-light text-neutral-800 mb-5">
                Aerial Yoga
              </h1>
              <p className="text-neutral-700 leading-relaxed mb-6">
                Yoga with a twist — literally. Using a fabric hammock suspended from the ceiling, 
                aerial yoga lets you explore poses from a completely different perspective. 
                Inversions become accessible, stretches go deeper, and there's something genuinely 
                playful about practicing in the air.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/contact" className="btn-primary">
                  Book a Session <ArrowRight className="w-4 h-4 inline ml-2" />
                </Link>
              </div>
              
              <div className="flex flex-wrap gap-4 mt-6 text-sm text-neutral-600">
                <span className="flex items-center gap-2">
                  <User className="w-4 h-4 text-gold" />
                  Private 1-to-1 only
                </span>
                <span className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-gold" />
                  Frome, Somerset
                </span>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <div className="aspect-[3/4] rounded-2xl overflow-hidden">
                <img 
                  src="https://seventhsensehealing.com/wp-content/uploads/2023/02/aerial-yoga-1-scaled-1.jpg"
                  alt="Aerial yoga practice with fabric hammock"
                  className="w-full h-full object-cover"
                  width={800}
                  height={600}
                  loading="eager"
                  decoding="async"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>
      
      {/* What Is Aerial Yoga */}
      <section className="py-16 px-6 md:px-12 bg-cream">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-6">
              What Makes Aerial Different
            </h2>
            <div className="prose-content space-y-4 text-neutral-700">
              <p>
                The hammock changes everything. Poses that might be difficult on the ground become 
                more accessible when you have support. Inversions — where your head is below your 
                heart — happen naturally and safely. Deep stretches go even deeper because you can 
                use your body weight and the hammock's support together.
              </p>
              <p>
                It's also unexpectedly fun. There's something about being slightly off the ground 
                that reconnects you with a sense of play. Adults don't get many chances to swing, 
                hang, and move through space like children do.
              </p>
              <p>
                I offer aerial yoga as <strong>private 1-to-1 sessions only</strong> in my yoga room 
                in Frome. This means you get my full attention, and we can progress at exactly the 
                right pace for you. If you're interested in group classes, I also offer <Link to="/yoga-classes/hatha-yoga" className="text-gold hover:underline">Hatha yoga</Link> and <Link to="/yoga-classes/yin-yoga" className="text-gold hover:underline">Yin yoga</Link>.
              </p>
            </div>
          </motion.div>
        </div>
      </section>
      
      {/* Benefits */}
      <section className="py-16 px-6 md:px-12 bg-muted">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-6 text-center">
              What Aerial Yoga Can Do
            </h2>
            <div className="bg-white/70 p-6 md:p-8 rounded-xl border border-gold/10">
              <ul className="space-y-4">
                <li className="flex items-start gap-3 text-neutral-700">
                  <Check className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" />
                  <span><strong>Spinal decompression</strong> — inversions allow the spine to lengthen and decompress, often relieving back tension.</span>
                </li>
                <li className="flex items-start gap-3 text-neutral-700">
                  <Check className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" />
                  <span><strong>Core strength</strong> — maintaining stability in the hammock engages your core in ways regular yoga doesn't.</span>
                </li>
                <li className="flex items-start gap-3 text-neutral-700">
                  <Check className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" />
                  <span><strong>Deeper stretches</strong> — the hammock supports your weight, letting you relax further into stretches.</span>
                </li>
                <li className="flex items-start gap-3 text-neutral-700">
                  <Check className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" />
                  <span><strong>Joint health</strong> — low-impact movement that's gentler on joints than many forms of exercise.</span>
                </li>
                <li className="flex items-start gap-3 text-neutral-700">
                  <Check className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" />
                  <span><strong>Mental benefits</strong> — the playful nature and focus required can be deeply calming for busy minds.</span>
                </li>
              </ul>
            </div>
          </motion.div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 px-6 md:px-12 bg-cream">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-8 text-center">
              Common Questions
            </h2>
            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <div key={index} className="bg-white/70 p-5 rounded-xl border border-gold/10">
                  <h3 className="font-heading text-lg text-neutral-800 mb-2">{faq.question}</h3>
                  <p className="text-neutral-600 text-sm">{faq.answer}</p>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>
      
      {/* CTA */}
      <section className="py-16 px-6 md:px-12 bg-muted">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-4">
              Want to Try Aerial?
            </h2>
            <p className="text-neutral-600 mb-6 max-w-xl mx-auto">
              Get in touch to book a private aerial yoga session. It's a unique experience — 
              and most people come out grinning.
            </p>
            <Link to="/contact" className="btn-primary">
              Book a Session <ArrowRight className="w-4 h-4 inline ml-2" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Related */}
      <section className="py-12 px-6 md:px-12 bg-cream">
        <div className="max-w-6xl mx-auto">
          <h3 className="font-heading text-xl text-neutral-800 mb-6 text-center">Also Available</h3>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/yoga-classes/hatha-yoga" className="text-sm text-gold hover:underline underline-offset-4">Hatha Yoga</Link>
            <span className="text-neutral-300">|</span>
            <Link to="/yoga-classes/yin-yoga" className="text-sm text-gold hover:underline underline-offset-4">Yin Yoga</Link>
            <span className="text-neutral-300">|</span>
            <Link to="/yoga-classes/private-yoga" className="text-sm text-gold hover:underline underline-offset-4">Private Sessions</Link>
            <span className="text-neutral-300">|</span>
            <Link to="/services" className="text-sm text-gold hover:underline underline-offset-4">All Services</Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AerialYogaFrome;
